import { SITE } from "@/content/site";
import { LOCATIONS } from "@/content/locations";
import { BUSINESS_HOURS_SPECIFICATION } from "@/content/business-hours";

/**
 * Site-wide JSON-LD blocks emitted from the root route. Pure data — kept
 * out of __root.tsx so the layout file stays focused on shell + providers.
 */

export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE.url}/#person`,
  name: SITE.fullName,
  alternateName: ["Tamal", "Freelancer Tamal", "SEO Tamal"],
  url: SITE.url,
  image: `${SITE.url}/og-image.jpg`,
  jobTitle: "SEO Expert & Digital Marketing Consultant",
  description: SITE.description,
  email: SITE.email,
  telephone: SITE.phone,
  worksFor: { "@type": "Organization", name: SITE.name },
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.streetAddress,
    addressLocality: SITE.city,
    addressRegion: SITE.region,
    postalCode: SITE.postalCode,
    addressCountry: SITE.countryCode,
  },
  hasOccupation: {
    "@type": "Occupation",
    name: "SEO, AEO & GEO Consultant",
    occupationLocation: {
      "@type": "City",
      name: SITE.city,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.streetAddress,
        addressRegion: SITE.region,
        postalCode: SITE.postalCode,
        addressCountry: SITE.countryCode,
      },
    },
    skills:
      "SEO, AEO, GEO, Technical SEO, Local SEO, E-commerce SEO, Content Strategy, Digital Marketing",
  },
  knowsLanguage: ["English", "Bengali"],
  knowsAbout: [
    "Search Engine Optimization",
    "Technical SEO",
    "Local SEO",
    "E-commerce SEO",
    "Answer Engine Optimization",
    "Generative Engine Optimization",
    "Content Strategy",
    "Digital Marketing Strategy",
    "Google Business Profile Optimization",
    "Schema Markup",
    "Core Web Vitals",
  ],
  sameAs: Object.values(SITE.social),
  // Author entity expansion: site-wide Person sameAs is sourced from SITE.social
  // so every page emits the same signal. To strengthen disambiguation in
  // Google's Knowledge Graph and LLM retrieval, add GitHub, X/Twitter,
  // Crunchbase, Medium/Substack, Wikidata Q-id, Search Engine Journal author
  // archive and conference speaker pages to SITE.social once each URL is live.
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE.url}/#business`,
  name: SITE.name,
  description: SITE.description,
  image: `${SITE.url}/og-image.jpg`,
  url: SITE.url,
  email: SITE.email,
  telephone: SITE.phone,
  priceRange: "$$",
  currenciesAccepted: "USD, EUR, GBP, BDT",
  paymentAccepted: "Bank Transfer, Wise, Payoneer, bKash",
  openingHoursSpecification: BUSINESS_HOURS_SPECIFICATION,
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.streetAddress,
    addressLocality: SITE.city,
    addressRegion: SITE.region,
    postalCode: SITE.postalCode,
    addressCountry: SITE.countryCode,
  },
  geo: { "@type": "GeoCoordinates", latitude: SITE.geo.lat, longitude: SITE.geo.lng },
  areaServed: [
    ...LOCATIONS.map((l) => ({
      "@type": "City",
      name: l.city,
      address: {
        "@type": "PostalAddress",
        addressRegion: l.region,
        addressCountry: l.countryCode,
      },
    })),
    { "@type": "Country", name: "Bangladesh" },
    { "@type": "Place", name: "Worldwide" },
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: SITE.phone,
      email: SITE.email,
      contactType: "sales",
      areaServed: ["BD", "US", "GB", "DE", "SG", "Worldwide"],
      availableLanguage: ["English", "Bengali"],
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "SEO, AEO, GEO & Digital Marketing Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Search Engine Optimization (SEO)", url: `${SITE.url}/services` } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Answer Engine Optimization (AEO)", url: `${SITE.url}/services/aeo` } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Generative Engine Optimization (GEO)", url: `${SITE.url}/services/geo` } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Digital Marketing Strategy", url: `${SITE.url}/services/digital-marketing` } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Local SEO & Google Business Profile", url: `${SITE.url}/seo-locations` } },
    ],
  },
  founder: { "@id": `${SITE.url}/#person` },
  sameAs: Object.values(SITE.social),
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  name: SITE.name,
  url: SITE.url,
  description: SITE.description,
  inLanguage: "en",
  publisher: { "@id": `${SITE.url}/#person` },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.url}/blog?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};
/**
 * The three sitewide entities merged into one `@graph`. Same nodes, same
 * `@id`s and cross-references — one `<script>` and one `@context` instead of
 * three, which trims ~0.4 KB of HTML from every page.
 */
const withoutContext = <T extends Record<string, unknown>>(schema: T) => {
  const { "@context": _context, ...rest } = schema;
  return rest;
};

export const rootGraphSchema = {
  "@context": "https://schema.org",
  "@graph": [
    withoutContext(personSchema),
    withoutContext(localBusinessSchema),
    withoutContext(websiteSchema),
  ],
};
